/** Notification copy shared by on-device schedules and server pushes. `{name}` placeholders are filled by `fillCopy`. */

export interface CopyTemplate {
  title: string;
  body: string;
}

export interface ScheduledCopy extends CopyTemplate {
  id: string;
  /** Minutes after the schedule starts. */
  afterMinutes: number;
  /** Optional local hour to snap to, so evening nudges never land at 3 am. */
  atHour?: number;
  deeplink: string;
}

export function fillCopy(template: string, values: Readonly<Record<string, string | number | null | undefined>>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = values[key];
    return value === null || value === undefined || value === "" ? match : String(value);
  });
}

/** Removes sentences that still contain an unfilled placeholder. */
export function completeCopy(template: string, values: Readonly<Record<string, string | number | null | undefined>>): string {
  return fillCopy(template, values)
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => !/\{\w+\}/.test(sentence))
    .join(" ")
    .trim();
}

const day = 24 * 60;

/** Before sign-in; delivered quietly through provisional authorization. */
export const funnelAbandonCopy: readonly ScheduledCopy[] = [
  {id: "funnel-1h", afterMinutes: 60, title: "Your money plan is 80% done 🌱", body: "Two more taps and Sprout shows you where your money really goes.", deeplink: "/start"},
  {id: "funnel-1d", afterMinutes: day, atHour: 19, title: "Still wondering where it all went? 👀", body: "Your personalized plan is waiting right where you left it.", deeplink: "/start"},
  {id: "funnel-3d", afterMinutes: 3 * day, atHour: 12, title: "Sprout saved your spot 💚", body: "Finish your plan in under a minute.", deeplink: "/start"}
];

/** Signed in without a subscription; cancelled as soon as an entitlement is active. */
export const nurtureCopy: readonly ScheduledCopy[] = [
  {id: "nurture-30m", afterMinutes: 30, title: "Your {personality} plan is ready 🌱", body: "Start free for 3 days and see your real numbers.", deeplink: "/paywall?placement=nurture_30m"},
  {id: "nurture-1d", afterMinutes: day, atHour: 19, title: "You guessed {guess} a month 👀", body: "Want the real number? Sprout works it out in about a minute.", deeplink: "/paywall?placement=nurture_1d"},
  {id: "nurture-2d", afterMinutes: 2 * day, atHour: 12, title: "Sprout is getting thirsty 💧", body: "Your garden and your streak are waiting for you.", deeplink: "/checkin"},
  {id: "nurture-4d", afterMinutes: 4 * day, atHour: 18, title: "Forgotten subscriptions? 🕵️", body: "Most people pay for something they forgot about. Sprout finds yours.", deeplink: "/paywall?placement=nurture_4d"},
  {id: "nurture-7d", afterMinutes: 7 * day, atHour: 19, title: "A whole week of guessing 🤔", body: "Connect your bank and let Sprout do the math.", deeplink: "/paywall?placement=nurture_7d"},
  {id: "nurture-14d", afterMinutes: 14 * day, atHour: 11, title: "Your Money Wrapped is locked 🎁", body: "Start your free trial to unwrap your real spending.", deeplink: "/paywall?placement=nurture_14d"},
  {id: "nurture-21d", afterMinutes: 21 * day, atHour: 19, title: "Still here for you 🌱", body: "3 days free. Cancel anytime in Settings.", deeplink: "/paywall?placement=nurture_21d"},
  {id: "nurture-30d", afterMinutes: 30 * day, atHour: 10, title: "New month, fresh start 🗓️", body: "Start this month knowing exactly where your money goes.", deeplink: "/paywall?placement=nurture_30d"}
];

/** Local fallback reminder when the server cannot push. */
export const dailyReminderCopy: CopyTemplate = {title: "Money Minute time ⏱️", body: "Water Sprout and keep your {streak}-day streak alive."};

export const pushCopy = {
  dailyRecap: {title: "Yesterday: {amount} 💸", body: "{comparison} Tap for your Money Minute."},
  dailyRecapEmpty: {title: "Money Minute time ⏱️", body: "Sprout is thirsty. One tap waters your garden."},
  dailyRecapNoSpend: {title: "Yesterday: a no-spend day 🌱", body: "Not a cent spent. Tap for your Money Minute."},
  streakRisk: {title: "Your {streak}-day streak ends at midnight 🔥", body: "One tap keeps it alive."},
  petThirsty: {title: "Sprout is thirsty 💧", body: "It’s been {days} days. Come water your garden."},
  weeklyWrapped: {title: "Your week in money is ready 🎁", body: "You spent {amount}. See your top spot and share your Wrapped."},
  monthlyWrapped: {title: "{month} Wrapped is here 🎉", body: "Your month in money, ready to unwrap."},
  transaction: {title: "{merchant} · {amount}", body: "Today so far: {today}."},
  largeTransaction: {title: "Big purchase: {amount}", body: "{merchant}. That’s {multiple}× your usual purchase."},
  /** The only subscription pushes Sprout sends; trials, renewals and cancellations stay silent. */
  paymentFailed: {title: "Your Sprout Plus payment didn’t go through", body: "Update your payment method to keep your accounts syncing."},
  cardExpiring: {title: "Your card for Sprout Plus is expiring", body: "Update it before your next renewal to keep your accounts syncing."},
  nurture: {title: "Your {personality} plan is waiting 🌱", body: "Start free for 3 days and see your real numbers."}
} as const satisfies Record<string, CopyTemplate>;
export type PushCopyKey = keyof typeof pushCopy;
